import * as React from "react";

import {compose} from "redux";
import {translate} from "react-i18next";
import {Dimmer, Loader} from "semantic-ui-react";

import {ModeltypeBasicDto, OptionDto, OptionTransferDto, UpdateDraftAssignmentsDto, WorkflowStatusDtoType} from "../../../services/Client";

import Dialog from "../../../components/Dialogs/Dialog";
import Button from "../../../components/Button";
import {DerivateAssignmentDraftTypes} from "../../../components/Table/components/derivate/DerivateUtils";

import DataAdapterOptionsView from "../DataAdapterOptionsView";
import {UDAStep, UDAStepHeader} from "./UDAStepHeader";
import {DerivatesSelectionStep} from "./DerivatesSelectionStep";
import {SelectAction, UDASummaryStep} from "./UDASummaryStep";
import {UDAErrorResultStep} from "./UDAErrorResultStep";
import {calculateUpdateItems, handleSelection} from "./UpdateDraftAssignmentsHelper";

interface IUpdateDraftAssignmentsDialogProps {

    /** Selected options */
    selectedItems: DataAdapterOptionsView[];

    /** Available derivates */
    derivates: ModeltypeBasicDto[];

    /** Error result after update */
    errorUpdateResult?: {[key: number]: string};

    /** Translation */
    t: reactI18Next.TranslationFunction;

    /** Close action */
    onClose: () => void;

    /** Refresh action */
    onRefresh: () => void;

    /** Update action */
    onSubmit: (value: UpdateDraftAssignmentsDto) => void;

    userLanguage: string;

}

interface IUpdateDraftAssignmentsDialogState {

    /** Current step */
    activeStep: UDAStep;

    /** Selected derivates */
    selectedDerivates: ModeltypeBasicDto[];

    /** Update action triggered */
    loaderActive: boolean;

    /** Selected options to be updated */
    updateItems: Map<OptionDto, boolean>;
}

class UpdateDraftAssignmentsDialog extends React.Component<IUpdateDraftAssignmentsDialogProps, IUpdateDraftAssignmentsDialogState> {

    constructor(props: IUpdateDraftAssignmentsDialogProps) {
        super(props);

        this.state = {
            activeStep: UDAStep.DerivatesSelectionStep,
            selectedDerivates: [],
            updateItems: new Map(),
            loaderActive: false
        };
    }

    componentDidUpdate(prevProps: Readonly<IUpdateDraftAssignmentsDialogProps>): void {
        if (prevProps.derivates !== this.props.derivates
            || prevProps.selectedItems !== this.props.selectedItems) {

            this.setState({
                selectedDerivates: [],
                updateItems: new Map(),
                activeStep: UDAStep.DerivatesSelectionStep,
            });
        }

        if (this.props.errorUpdateResult && prevProps.errorUpdateResult !== this.props.errorUpdateResult) {
            this.setState({
                activeStep: UDAStep.ResultStep,
                loaderActive: false
            });
        }
    }

    processInitialUpdateItems = () => {
        this.setState({
            updateItems: calculateUpdateItems(this.props.selectedItems, this.state.selectedDerivates)
        });
    }

    handleOptionSelectChange = (items: DataAdapterOptionsView[], resetFlag: boolean, actionType: SelectAction) => {
        this.setState({
            updateItems: handleSelection(items, this.state.updateItems, resetFlag, actionType)
        });
    }

    handleSubmit = () => {
        if (this.state.selectedDerivates.length > 0 && this.state.updateItems.size > 0) {
            this.setState({loaderActive: true});

            const reqOptions: OptionTransferDto[] = [];
            this.state.updateItems.forEach((value: boolean, key: OptionDto) => {
                const a: OptionTransferDto = {
                    optionDto: key,
                    resetFlag: value
                };

                reqOptions.push(a);
            });

            const reqParam: UpdateDraftAssignmentsDto = {
                options: reqOptions,
                derivates: this.state.selectedDerivates
            };

            this.props.onSubmit(reqParam);
        }
    }

    render() {
        const {t} = this.props;
        const dlgButton = this.state.activeStep === UDAStep.ResultStep
            ? (
                <Button
                    key={"checkButton"}
                    icon={"check"}
                    onClick={this.props.onRefresh}
                    disabled={this.state.loaderActive}
                />
            )
            : (
                <Button
                    key={"cancelButton"}
                    icon={"ban"}
                    onClick={this.props.onClose}
                    disabled={this.state.loaderActive}
                />
            );

        const startedWfItems: DataAdapterOptionsView[] = [];
        const initialWfStepItems: DataAdapterOptionsView[] = [];
        const invalidItems: DataAdapterOptionsView[] = [];

        this.props.selectedItems.map((item: DataAdapterOptionsView) => {
            if (item.dto.modeltypes &&
                item.dto.modeltypes.find(der => this.state.selectedDerivates.find(d => d.objectId === der.modeltypeBasic.objectId) !== undefined &&
                    DerivateAssignmentDraftTypes.has(der.assignmentType)) !== undefined) {
                if (item.statusValueStates) {
                    if (item.statusView === WorkflowStatusDtoType.MAINTENANCE) {
                        initialWfStepItems.push(item);
                    } else {
                        startedWfItems.push(item);
                    }
                }
            } else {
                invalidItems.push(item);
            }
        });

        return (
            <Dialog
                open={true}
                title={t("UPDATE_DRAFT_ASGN_TITLE")}
                customStyleClassName={'multi-step-dialog'}
                actions={[dlgButton]}
            >
                <div style={{marginBottom: '30px'}}>
                    <UDAStepHeader
                        activeStep={this.state.activeStep}
                        t={t}
                    />
                </div>

                {/** First step start */}
                {this.state.activeStep === UDAStep.DerivatesSelectionStep &&
                    <DerivatesSelectionStep
                        allDerivates={this.props.derivates}
                        selectedDerivates={this.state.selectedDerivates}
                        t={t}
                        onChange={(value: ModeltypeBasicDto[]) => this.setState({selectedDerivates: value}, () => this.processInitialUpdateItems())}
                        onStepForward={() => this.setState({activeStep: UDAStep.SummaryStep})}
                        userLanguage={this.props.userLanguage}
                    />
                }
                {/** First step end */}

                {/** Second step start */}
                {
                    this.state.activeStep === UDAStep.SummaryStep &&
                    <UDASummaryStep
                        derivates={this.state.selectedDerivates}
                        maintenanceStepItems={initialWfStepItems}
                        startedWfItems={startedWfItems}
                        invalidItems={invalidItems}
                        updateItems={this.state.updateItems}
                        t={t}
                        onStepBackwards={() => this.setState({activeStep: UDAStep.DerivatesSelectionStep})}
                        onStepForward={() => this.setState({activeStep: UDAStep.ConfirmStep})}
                        handleSelectChange={this.handleOptionSelectChange}
                        userLanguage={this.props.userLanguage}
                    />
                }
                {/** Second step end */}

                {/** Third step start */}
                {
                    this.state.activeStep === UDAStep.ConfirmStep && this.state.updateItems.size > 0 &&
                    <>
                        {this.state.loaderActive &&
                            <Dimmer active={this.state.loaderActive}>
                                <Loader>{t("UPDATE_DRF_ASGN:UPDATE_ASSIGNMENTS")}</Loader>
                            </Dimmer>
                        }
                        <div style={{marginBottom: '20px'}}>
                            <p>{t("UPDATE_DRF_ASGN:SUBMIT_TOTAL_TXT") + ' '} <b>{this.state.updateItems.size}</b></p>
                            <p>{t("UPDATE_DRF_ASGN:SUBMIT_RESET_TXT") + ' '}
                                <b style={{color: 'red'}}>{Array.from(this.state.updateItems.values()).filter((val => val)).length}</b>
                            </p>
                        </div>
                        <div style={{marginTop: '30px'}}>
                            <Button
                                icon={'arrow left'}
                                onClick={() => this.setState({activeStep: UDAStep.SummaryStep})}
                            />
                            <Button
                                icon={'arrow right'}
                                style={{float: 'right'}}
                                onClick={this.handleSubmit}
                            />
                        </div>
                    </>
                }
                {/** Third step end */}

                {/** Fourth step start */}
                <>
                    {this.state.activeStep === UDAStep.ResultStep && this.props.errorUpdateResult &&
                        <div>
                            <p>
                                <b>{this.state.updateItems.size - Object.keys(this.props.errorUpdateResult).length}</b>
                                {' ' + t("UPDATE_DRF_ASGN:RESULT_SUCCESS")}
                            </p>
                            {
                                Object.keys(this.props.errorUpdateResult).length > 0 &&
                                <UDAErrorResultStep
                                    result={this.props.errorUpdateResult}
                                    items={Array.from(this.state.updateItems.keys())}
                                    t={t}
                                />
                            }
                        </div>
                    }
                </>
                {/** Fourth step end */}
            </Dialog >
        );
    }
}

export default compose<React.ComponentType<IUpdateDraftAssignmentsDialogProps>>(
    translate("UPDATE_DRF_ASGN")
)(UpdateDraftAssignmentsDialog);



// WEBPACK FOOTER //
// ./src/views/OptionsView/updateDraftAssignments/UpdateDraftAssignmentsDialog.tsx